// Logements : liste avec filtres, création et modification, fiche avec onglets (F-002, F-007).
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { del, post, put } from "@core/api";
import { date, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { extensions } from "@core/modules";
import { useOrg, useRestricted } from "@core/org";
import {
  Button,
  ButtonLink,
  Card,
  Checkbox,
  Empty,
  ErrorBox,
  Input,
  KeyValues,
  Loading,
  Modal,
  MoneyInput,
  Notice,
  PageHeader,
  Select,
  Table,
  Tabs,
  Textarea,
  fieldError,
  useToast,
} from "@core/ui";
import {
  AddressFields,
  CHILD_KINDS,
  CLASS_OPTIONS,
  DiagChip,
  EMPTY_ADDRESS,
  KIND_OPTIONS,
  OccupationBadge,
  TRI_OPTIONS,
  VerifChip,
  intOrNull,
  numOrNull,
  str,
  triLabel,
  yesNo,
  type Address,
  type Property,
} from "./shared";
import { DiagnosticsTab } from "./diagnostics";
import { LocalRulesTab } from "./localRules";
import { OwnersTab } from "./propertyOwners";
import { VerificationTab } from "./verification";

// ---------------------------------------------------------------------------
// Liste

export function PropertiesListPage() {
  const org = useOrg();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const q = params.get("q") ?? "";
  const kind = params.get("type") ?? "";
  const etat = params.get("etat") ?? "actifs";
  const occupation = params.get("occupation") ?? "";
  const query = new URLSearchParams();
  if (q) query.set("q", q);
  if (kind) query.set("kind", kind);
  if (etat !== "actifs") query.set("etat", etat);
  if (occupation) query.set("occupation", occupation);
  const list = useGet<{ items: Property[]; quota: { used: number; limit: number | null } }>(org.api(`/properties${query.toString() ? `?${query}` : ""}`));
  const setFilter = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const items = list.data?.items ?? [];
  const quota = list.data?.quota;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Location"
        title="Logements"
        intro="Vos logements, chambres et dépendances, leur occupation, leurs diagnostics et leurs règles locales."
        actions={
          org.can("operate") ? (
            <>
              <ButtonLink to={org.to("logements/importer-une-location")} variant="secondaire">
                Reprendre une location en cours
              </ButtonLink>
              <Button onClick={() => setCreating(true)}>Ajouter un logement</Button>
            </>
          ) : null
        }
      />
      {quota ? (
        <p className="small muted">
          {quota.limit !== null
            ? `${quota.used} logement${quota.used > 1 ? "s" : ""} sur ${quota.limit} permis par votre offre. Les chambres et dépendances rattachées ne sont pas décomptées.`
            : `${quota.used} logement${quota.used > 1 ? "s" : ""}.`}
        </p>
      ) : null}
      <Card>
        <div className="form-row">
          <Input label="Rechercher" type="search" placeholder="Nom ou adresse" value={q} onChange={(e) => setFilter("q", e.target.value)} />
          <Select label="Type" value={kind} options={[{ value: "", label: "Tous les types" }, ...KIND_OPTIONS]} onChange={(e) => setFilter("type", e.target.value)} />
          <Select
            label="Occupation"
            value={occupation}
            options={[
              { value: "", label: "Toutes" },
              { value: "vacant", label: "Vacants" },
              { value: "occupe", label: "Loués ou réservés" },
            ]}
            onChange={(e) => setFilter("occupation", e.target.value)}
          />
          <Select
            label="État"
            value={etat}
            options={[
              { value: "actifs", label: "En gestion" },
              { value: "archives", label: "Archivés" },
              { value: "tous", label: "Tous" },
            ]}
            onChange={(e) => setFilter("etat", e.target.value === "actifs" ? "" : e.target.value)}
          />
        </div>
      </Card>
      <ErrorBox error={list.error} />
      {list.isLoading ? <Loading /> : null}
      {!list.isLoading && items.length === 0 ? (
        <Empty
          title={q || kind || occupation || etat !== "actifs" ? "Aucun logement ne correspond à ces filtres." : "Aucun logement pour le moment."}
          action={org.can("operate") && !q && !kind ? <Button variant="secondaire" onClick={() => setCreating(true)}>Ajouter un logement</Button> : undefined}
        >
          Ajoutez un logement pour ouvrir une campagne de location ou reprendre une location déjà en cours.
        </Empty>
      ) : null}
      {items.length ? (
        <Table caption="Logements">
          <thead>
            <tr>
              <th>Bien</th>
              <th>Adresse</th>
              <th>Surface</th>
              <th>DPE</th>
              <th>Occupation</th>
              <th>Diagnostics</th>
              <th>Bailleur</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td>
                  <Link to={org.to(`logements/${p.id}`)}>{p.label}</Link>
                  <span className="pat-sub">
                    {p.kind_label}
                    {p.parent_label ? ` · rattaché à ${p.parent_label}` : ""}
                    {p.building_name ? ` · ${p.building_name}` : ""}
                    {p.furnished ? " · meublé" : ""}
                    {p.archived_at ? " · archivé" : ""}
                  </span>
                </td>
                <td className="small">{p.address_line || "—"}</td>
                <td className="num">
                  {p.surface_m2 !== null ? `${p.surface_m2.toLocaleString("fr-FR")} m²` : "—"}
                  {p.rooms ? <span className="pat-sub">{p.rooms} pièce{p.rooms > 1 ? "s" : ""}</span> : null}
                </td>
                <td>{p.dpe_class ?? "—"}</td>
                <td>
                  <OccupationBadge occupation={p.occupation} />
                  {p.open_campaigns ? <span className="pat-sub">Campagne ouverte</span> : null}
                </td>
                <td>
                  {p.diagnostics ? (
                    p.diagnostics.expired ? (
                      <DiagChip status="echu" label={`${p.diagnostics.expired} échu${p.diagnostics.expired > 1 ? "s" : ""}`} />
                    ) : p.diagnostics.soon ? (
                      <DiagChip status="bientot_echu" label={`${p.diagnostics.soon} échéance${p.diagnostics.soon > 1 ? "s" : ""} proche${p.diagnostics.soon > 1 ? "s" : ""}`} />
                    ) : p.diagnostics.missing ? (
                      <DiagChip status="manquant" label={`${p.diagnostics.missing} manquant${p.diagnostics.missing > 1 ? "s" : ""}`} />
                    ) : p.diagnostics.to_check ? (
                      <DiagChip status="a_verifier" label={`${p.diagnostics.to_check} à vérifier`} />
                    ) : (
                      <DiagChip status="valide" label="À jour" />
                    )
                  ) : (
                    <span className="muted small">—</span>
                  )}
                </td>
                <td>{p.kind === "logement" ? <VerifChip status={p.verification_status} /> : <span className="muted small">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      <Modal open={creating} onClose={() => setCreating(false)} title="Ajouter un logement">
        {creating ? (
          <PropertyForm
            onDone={(p) => {
              setCreating(false);
              nav(org.to(`logements/${p.id}`));
            }}
            onCancel={() => setCreating(false)}
          />
        ) : null}
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulaire de création / modification

type FormState = {
  kind: string;
  label: string;
  address: Address;
  building_id: string;
  parent_id: string;
  owner_id: string;
  surface_m2: string;
  rooms: string;
  furnished: boolean;
  dpe_class: string;
  ges_class: string;
  floor: string;
  lot_number: string;
  tantiemes: string;
  construction_year: string;
  heating: string;
  hot_water: string;
  equipments: string;
  rent_hint_cents: number | null;
  charges_hint_cents: number | null;
  description: string;
  electricity_year: string;
  gas: string;
  gas_year: string;
  risk_zone: string;
  noise_zone: string;
};

function initialState(p?: Property, preset?: Partial<FormState>): FormState {
  const d = p?.details ?? {};
  return {
    kind: p?.kind ?? "logement",
    label: p?.label ?? "",
    address: p?.address ? { ...EMPTY_ADDRESS, ...p.address } : EMPTY_ADDRESS,
    building_id: p?.building_id ?? "",
    parent_id: p?.parent_id ?? "",
    owner_id: "",
    surface_m2: p?.surface_m2 !== null && p?.surface_m2 !== undefined ? String(p.surface_m2).replace(".", ",") : "",
    rooms: str(p?.rooms),
    furnished: p?.furnished ?? false,
    dpe_class: p?.dpe_class ?? "",
    ges_class: str(d.ges_class),
    floor: str(d.floor),
    lot_number: str(d.lot_number),
    tantiemes: str(d.tantiemes),
    construction_year: str(d.construction_year),
    heating: str(d.heating),
    hot_water: str(d.hot_water),
    equipments: Array.isArray(d.equipments) ? d.equipments.join(", ") : "",
    rent_hint_cents: d.rent_hint_cents ?? null,
    charges_hint_cents: d.charges_hint_cents ?? null,
    description: str(d.description),
    electricity_year: str(d.electricity_year),
    gas: d.gas ?? "inconnu",
    gas_year: str(d.gas_year),
    risk_zone: d.risk_zone ?? "inconnu",
    noise_zone: d.noise_zone ?? "inconnu",
    ...preset,
  };
}

export function PropertyForm({ property, preset, onDone, onCancel }: { property?: Property; preset?: Partial<FormState>; onDone: (p: Property) => void; onCancel: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [f, setF] = useState<FormState>(() => initialState(property, preset));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const buildings = useGet<{ items: { id: string; name: string; archived_at: string | null }[] }>(org.api("/buildings"));
  const owners = useGet<{ items: { id: string; display_name: string; kind_label: string; archived_at: string | null }[] }>(property ? null : org.api("/owners"));
  const parents = useGet<{ items: Property[] }>(org.api("/properties"));
  const isChild = CHILD_KINDS.includes(f.kind);
  const parentOptions = (parents.data?.items ?? []).filter((p) => ["logement", "local_commercial", "local_professionnel"].includes(p.kind) && !p.parent_id && p.id !== property?.id);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const err = (k: string) => fieldError(error, k);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const surface = numOrNull(f.surface_m2);
    const body = {
      kind: f.kind,
      label: f.label,
      address: f.address,
      building_id: f.building_id || null,
      parent_id: isChild ? f.parent_id || null : null,
      owner_id: property ? undefined : f.owner_id || null,
      surface_m2: surface,
      rooms: intOrNull(f.rooms),
      furnished: f.furnished,
      dpe_class: f.dpe_class || null,
      ges_class: f.ges_class || null,
      details: {
        floor: f.floor,
        lot_number: f.lot_number,
        tantiemes: intOrNull(f.tantiemes),
        construction_year: intOrNull(f.construction_year),
        heating: f.heating,
        hot_water: f.hot_water,
        equipments: f.equipments.split(",").map((s) => s.trim()).filter(Boolean),
        rent_hint_cents: f.rent_hint_cents,
        charges_hint_cents: f.charges_hint_cents,
        description: f.description,
        electricity_year: intOrNull(f.electricity_year),
        gas: f.gas,
        gas_year: f.gas === "oui" ? intOrNull(f.gas_year) : null,
        risk_zone: f.risk_zone,
        noise_zone: f.noise_zone,
      },
    };
    if ([body.surface_m2, body.rooms, body.details.tantiemes, body.details.construction_year, body.details.electricity_year, body.details.gas_year].some((n) => Number.isNaN(n as number))) {
      setBusy(false);
      setError(new Error("Un champ numérique contient autre chose qu’un nombre."));
      return;
    }
    try {
      const saved = property ? await put<Property>(org.api(`/properties/${property.id}`), body) : await post<Property>(org.api("/properties"), body);
      invalidate(org.api("/properties"), org.api("/buildings"), org.api("/patrimoine"));
      toast(property ? "Logement mis à jour." : "Logement ajouté.");
      onDone(saved);
    } catch (e2) {
      setError(e2);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={submit} noValidate>
      <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
      {(error as any)?.fields?.length ? <Notice tone="erreur">Certains champs sont à corriger : ils sont signalés ci-dessous.</Notice> : null}
      <div className="form-row">
        <Select label="Type de bien" value={f.kind} options={KIND_OPTIONS} error={err("kind")} onChange={(e) => set("kind", e.target.value)} />
        <Input label="Nom du bien" required value={f.label} error={err("label")} placeholder="Ex. T2 rue des Tanneurs" onChange={(e) => set("label", e.target.value)} />
      </div>
      {isChild ? (
        <Select
          label="Rattaché au logement"
          help={f.kind === "chambre" ? "Une chambre se rattache au logement qui la contient." : "Laissez vide pour une dépendance louée séparément ; rattachez-la pour qu’elle soit louée avec le logement."}
          value={f.parent_id}
          error={err("parent_id")}
          options={[{ value: "", label: f.kind === "chambre" ? "Choisissez un logement" : "Aucun : louée séparément" }, ...parentOptions.map((p) => ({ value: p.id, label: p.label }))]}
          onChange={(e) => set("parent_id", e.target.value)}
        />
      ) : null}
      <Select
        label="Immeuble"
        help="Facultatif. Pour un bien rattaché, l’immeuble du logement est repris."
        value={f.building_id}
        error={err("building_id")}
        options={[{ value: "", label: "Aucun immeuble" }, ...(buildings.data?.items ?? []).filter((b) => !b.archived_at || b.id === f.building_id).map((b) => ({ value: b.id, label: b.name }))]}
        onChange={(e) => set("building_id", e.target.value)}
      />
      <AddressFields
        value={f.address}
        onChange={(a) => set("address", a)}
        required={!isChild && !f.building_id}
        help={isChild || f.building_id ? "Laissée vide, l’adresse du logement ou de l’immeuble est reprise." : undefined}
        errors={{ line1: err("address.line1"), postal_code: err("address.postal_code"), city: err("address.city") }}
      />
      <div className="form-row">
        <Input label="Surface habitable (m²)" inputMode="decimal" value={f.surface_m2} error={err("surface_m2")} onChange={(e) => set("surface_m2", e.target.value)} />
        <Input label="Nombre de pièces" inputMode="numeric" value={f.rooms} error={err("rooms")} onChange={(e) => set("rooms", e.target.value)} />
        <Select label="Classe DPE" value={f.dpe_class} options={CLASS_OPTIONS} error={err("dpe_class")} onChange={(e) => set("dpe_class", e.target.value)} />
        <Select label="Classe GES" value={f.ges_class} options={CLASS_OPTIONS} error={err("ges_class")} onChange={(e) => set("ges_class", e.target.value)} />
      </div>
      <Checkbox label="Logement meublé" checked={f.furnished} onChange={(v) => set("furnished", v)} />
      {!property ? (
        <Select
          label="Propriétaire"
          help="Facultatif : détenteur à 100 %. Les quotes-parts se règlent ensuite dans l’onglet « Propriétaires et quotes-parts »."
          value={f.owner_id}
          error={err("owner_id")}
          options={[{ value: "", label: "À préciser plus tard" }, ...(owners.data?.items ?? []).filter((o) => !o.archived_at).map((o) => ({ value: o.id, label: `${o.display_name} (${o.kind_label})` }))]}
          onChange={(e) => set("owner_id", e.target.value)}
        />
      ) : null}
      <fieldset className="pat-fieldset">
        <legend>Description et équipements</legend>
        <div className="form-row">
          <Input label="Étage" value={f.floor} onChange={(e) => set("floor", e.target.value)} />
          <Input label="Numéro de lot" value={f.lot_number} onChange={(e) => set("lot_number", e.target.value)} />
          <Input label="Tantièmes" inputMode="numeric" value={f.tantiemes} error={err("details.tantiemes")} onChange={(e) => set("tantiemes", e.target.value)} />
        </div>
        <div className="form-row">
          <Input label="Chauffage" value={f.heating} placeholder="Ex. individuel électrique" onChange={(e) => set("heating", e.target.value)} />
          <Input label="Eau chaude" value={f.hot_water} onChange={(e) => set("hot_water", e.target.value)} />
        </div>
        <Input label="Équipements" help="Séparés par des virgules." value={f.equipments} onChange={(e) => set("equipments", e.target.value)} />
        <Textarea label="Description" value={f.description} onChange={(e) => set("description", e.target.value)} />
        <div className="form-row">
          <MoneyInput label="Loyer indicatif hors charges" cents={f.rent_hint_cents} onChange={(c) => set("rent_hint_cents", c)} help="Pour la prochaine mise en location. Ne modifie aucun bail signé." />
          <MoneyInput label="Charges indicatives" cents={f.charges_hint_cents} onChange={(c) => set("charges_hint_cents", c)} />
        </div>
      </fieldset>
      {!isChild ? (
        <fieldset className="pat-fieldset">
          <legend>Informations utiles aux diagnostics</legend>
          <p className="small muted">Elles déterminent les diagnostics exigés. « Je ne sais pas » reste affiché comme une information à vérifier.</p>
          <div className="form-row">
            <Input label="Année de construction" inputMode="numeric" value={f.construction_year} error={err("details.construction_year")} onChange={(e) => set("construction_year", e.target.value)} />
            <Input label="Année de l’installation électrique" inputMode="numeric" value={f.electricity_year} error={err("details.electricity_year")} onChange={(e) => set("electricity_year", e.target.value)} />
          </div>
          <div className="form-row">
            <Select label="Installation de gaz" value={f.gas} options={TRI_OPTIONS} onChange={(e) => set("gas", e.target.value)} />
            {f.gas === "oui" ? <Input label="Année de l’installation de gaz" inputMode="numeric" value={f.gas_year} error={err("details.gas_year")} onChange={(e) => set("gas_year", e.target.value)} /> : null}
          </div>
          <div className="form-row">
            <Select label="Zone ou périmètre à risques" value={f.risk_zone} options={TRI_OPTIONS} onChange={(e) => set("risk_zone", e.target.value)} />
            <Select label="Zone de bruit d’un aéroport" value={f.noise_zone} options={TRI_OPTIONS} onChange={(e) => set("noise_zone", e.target.value)} />
          </div>
        </fieldset>
      ) : null}
      <div className="form-actions">
        <Button variant="discret" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" busy={busy}>
          {property ? "Enregistrer" : "Ajouter le logement"}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Fiche logement

type PropertyDetail = Property & {
  can_edit: boolean;
  building: { id: string; name: string; in_copropriete: boolean } | null;
  parent: { id: string; label: string; kind_label: string } | null;
  children: Property[];
  owners: { owner_id: string; display_name: string; kind_label: string; share_label: string }[];
  leases: { id: string; status: string; regime: string; start_on: string; end_on: string | null; rent_cents: number; charges_cents: number; deposit_cents: number; imported: boolean; tenants: string | null }[];
  campaigns: { id: string; title: string; status: string; rent_cents: number; created_at: string }[];
  dpe_rule?: { status: string; message: string };
  local_rules: { zone_tendue: string; encadrement: string; permit_regime: string; procedure_status: string };
};

const LEASE_STATUS: Record<string, string> = {
  brouillon: "Brouillon",
  en_signature: "En signature",
  signe: "Signé",
  actif: "En cours",
  preavis: "Préavis",
  termine: "Terminé",
  resilie: "Résilié",
};

const CAMPAIGN_STATUS: Record<string, string> = { brouillon: "Brouillon", ouverte: "Ouverte", selection: "Sélection en cours", attribuee: "Attribuée", close: "Close" };

export function PropertyPage() {
  const org = useOrg();
  const { propertyId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<PropertyDetail>(org.api(`/properties/${propertyId}`));
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const restricted = useRestricted();
  const ext = useMemo(() => extensions("propertyTabs", { data: null, restricted }), [restricted]);
  const p = q.data;
  const tab = params.get("onglet") ?? "informations";
  const setTab = (t: string) => {
    const next = new URLSearchParams(params);
    next.set("onglet", t);
    setParams(next, { replace: true });
  };
  if (q.isLoading) return <Loading />;
  if (q.error || !p) {
    return (
      <div className="page">
        <ErrorBox error={q.error ?? new Error("Logement introuvable.")} />
        <p>
          <Link to={org.to("logements")}>Revenir aux logements</Link>
        </p>
      </div>
    );
  }
  const housing = ["logement", "local_commercial", "local_professionnel"].includes(p.kind);
  const items: { value: string; label: string }[] = [
    { value: "informations", label: "Informations" },
    { value: "proprietaires", label: "Propriétaires et quotes-parts" },
    ...(housing ? [{ value: "diagnostics", label: "Diagnostics" }] : []),
    ...(p.kind !== "cave" && p.kind !== "parking" ? [{ value: "regles-locales", label: "Règles locales" }] : []),
    { value: "verification", label: "Vérification du bailleur" },
    ...ext.map((t) => ({ value: `ext-${t.id}`, label: t.label })),
  ];
  const current = items.some((i) => i.value === tab) ? tab : "informations";
  const extTab = ext.find((t) => `ext-${t.id}` === current);
  return (
    <div className="page">
      <PageHeader
        eyebrow={`${p.kind_label}${p.building ? ` · ${p.building.name}` : ""}`}
        title={p.label}
        intro={p.address_line || undefined}
        actions={
          p.can_edit ? (
            p.archived_at ? (
              <Button
                variant="secondaire"
                onClick={async () => {
                  try {
                    await post(org.api(`/properties/${p.id}/restore`));
                    invalidate(org.api("/properties"), org.api("/patrimoine"));
                    toast("Logement restauré.");
                  } catch (e: any) {
                    toast(e.message, "erreur");
                  }
                }}
              >
                Restaurer
              </Button>
            ) : (
              <>
                {p.kind === "logement" && p.occupation?.status === "vacant" ? (
                  <ButtonLink to={org.to(`logements/importer-une-location?logement=${p.id}`)} variant="secondaire">
                    Reprendre une location en cours
                  </ButtonLink>
                ) : null}
                <Button variant="secondaire" onClick={() => setArchiving(true)}>
                  Archiver
                </Button>
                <Button onClick={() => setEditing(true)}>Modifier</Button>
              </>
            )
          ) : null
        }
      />
      {p.archived_at ? (
        <Notice tone="attention" title="Logement archivé">
          Archivé le {date(p.archived_at)}
          {p.details?.archive?.reason ? ` (${ARCHIVE_REASONS.find((r) => r.value === p.details.archive.reason)?.label.toLowerCase()})` : ""}. Son historique (baux, campagnes, pièces) est conservé.
        </Notice>
      ) : null}
      <Tabs value={current} onChange={setTab} items={items} />
      {current === "informations" ? <InfoTab p={p} /> : null}
      {current === "proprietaires" ? <OwnersTab propertyId={p.id} canEdit={p.can_edit && !p.archived_at} /> : null}
      {current === "diagnostics" ? <DiagnosticsTab propertyId={p.id} canEdit={p.can_edit && !p.archived_at} /> : null}
      {current === "regles-locales" ? <LocalRulesTab propertyId={p.id} canEdit={p.can_edit && !p.archived_at} /> : null}
      {current === "verification" ? <VerificationTab propertyId={p.id} canEdit={p.can_edit && !p.archived_at} /> : null}
      {extTab ? <extTab.Component orgId={org.orgId} propertyId={p.id} /> : null}
      <Modal open={editing} onClose={() => setEditing(false)} title="Modifier le logement">
        {editing ? <PropertyForm property={p} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} /> : null}
      </Modal>
      <ArchiveModal open={archiving} property={p} onClose={() => setArchiving(false)} />
    </div>
  );
}

const ARCHIVE_REASONS = [
  { value: "vendu", label: "Vendu" },
  { value: "sortie_gestion", label: "Sorti de la gestion" },
  { value: "autre", label: "Autre raison" },
];

function ArchiveModal({ open, property, onClose }: { open: boolean; property: PropertyDetail; onClose: () => void }) {
  const org = useOrg();
  const nav = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [form, setForm] = useState({ reason: "vendu", on: "", note: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Archiver ce bien"
      footer={
        <>
          <Button
            variant="danger"
            onClick={async () => {
              setError(null);
              try {
                await del(org.api(`/properties/${property.id}`));
                invalidate(org.api("/properties"), org.api("/patrimoine"), org.api("/buildings"));
                toast("Fiche supprimée.");
                onClose();
                nav(org.to("logements"));
              } catch (e) {
                setError(e);
              }
            }}
          >
            Supprimer la fiche créée par erreur
          </Button>
          <Button variant="discret" onClick={onClose}>
            Annuler
          </Button>
          <Button
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await post(org.api(`/properties/${property.id}/archive`), { reason: form.reason, on: form.on || null, note: form.note });
                invalidate(org.api("/properties"), org.api("/patrimoine"), org.api("/buildings"));
                toast("Bien archivé. Son historique est conservé.");
                onClose();
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            Archiver
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>Le bien sort de la liste des logements gérés. Ses baux, campagnes, pièces et diagnostics restent consultables ; vous pourrez le restaurer.</p>
        <p className="small muted">La suppression définitive n’est possible que pour une fiche sans aucun historique (créée par erreur).</p>
        {property.children?.some((c) => !c.archived_at) ? <Notice tone="attention">Ses chambres et dépendances rattachées seront archivées avec lui.</Notice> : null}
        <ErrorBox error={error} />
        <Select label="Motif" value={form.reason} options={ARCHIVE_REASONS} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        <Input label="Date" type="date" value={form.on} onChange={(e) => setForm({ ...form, on: e.target.value })} />
        <Textarea label="Précision (facultatif)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </div>
    </Modal>
  );
}

function InfoTab({ p }: { p: PropertyDetail }) {
  const org = useOrg();
  const [adding, setAdding] = useState(false);
  const d = p.details ?? {};
  const canAddChild = p.can_edit && !p.archived_at && ["logement", "local_commercial", "local_professionnel"].includes(p.kind);
  return (
    <div className="stack">
      <div className="grid-2">
        <Card title="Caractéristiques">
          <KeyValues
            items={[
              ["Type", p.kind_label],
              ["Surface habitable", p.surface_m2 !== null ? `${p.surface_m2.toLocaleString("fr-FR")} m²` : "Non renseignée"],
              ["Pièces", p.rooms ?? "—"],
              ["Meublé", yesNo(p.furnished)],
              ["Classe DPE", p.dpe_class ?? "Non renseignée"],
              ["Classe GES", d.ges_class ?? "Non renseignée"],
              ["Étage", d.floor ?? "—"],
              ["Lot", d.lot_number ?? "—"],
              ["Tantièmes", d.tantiemes ?? "—"],
              ["Chauffage", d.heating ?? "—"],
              ["Eau chaude", d.hot_water ?? "—"],
              ["Équipements", Array.isArray(d.equipments) && d.equipments.length ? d.equipments.join(", ") : "—"],
            ]}
          />
          {d.description ? <p>{d.description}</p> : null}
        </Card>
        <div className="stack">
          <Card title="Occupation et location">
            <KeyValues
              items={[
                ["Occupation", <OccupationBadge key="o" occupation={p.occupation} />],
                ["Loyer indicatif", d.rent_hint_cents !== null && d.rent_hint_cents !== undefined ? `${money(d.rent_hint_cents)} hors charges` : "—"],
                ["Charges indicatives", d.charges_hint_cents !== null && d.charges_hint_cents !== undefined ? money(d.charges_hint_cents) : "—"],
                ["Rattaché à", p.parent ? <Link key="p" to={org.to(`logements/${p.parent.id}`)}>{p.parent.label}</Link> : "—"],
                ["Immeuble", p.building ? <Link key="b" to={org.to(`immeubles/${p.building.id}`)}>{p.building.name}</Link> : "—"],
                ["Décompté dans l’offre", p.counted ? "Oui" : "Non (chambre ou dépendance)"],
              ]}
            />
            <p className="small muted">Le loyer indicatif sert à la prochaine mise en location : le modifier ne change aucun bail signé.</p>
          </Card>
          <Card title="Règles locales saisies">
            <KeyValues
              items={[
                ["Zone tendue", triLabel(p.local_rules.zone_tendue)],
                ["Encadrement des loyers", triLabel(p.local_rules.encadrement)],
                ["Démarche de mise en location", p.local_rules.permit_regime === "inconnu" ? "Non renseignée" : p.local_rules.permit_regime === "aucun" ? "Aucune" : p.local_rules.permit_regime === "declaration" ? "Déclaration" : "Autorisation préalable"],
              ]}
            />
          </Card>
          {p.owners.length ? (
            <Card title="Propriétaires">
              <ul className="list-plain">
                {p.owners.map((o) => (
                  <li key={o.owner_id}>
                    <Link to={org.to(`proprietaires/${o.owner_id}`)}>{o.display_name}</Link> <span className="muted small">· {o.kind_label} · {o.share_label}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>
      {["logement", "local_commercial", "local_professionnel"].includes(p.kind) ? (
        <Card title="Chambres et dépendances" actions={canAddChild ? <Button small variant="secondaire" onClick={() => setAdding(true)}>Ajouter</Button> : null}>
          {p.children.length === 0 ? (
            <p className="muted">Aucune chambre ni dépendance rattachée. Une cave ou un parking rattaché est loué avec ce logement ; une chambre peut être louée séparément (colocation).</p>
          ) : (
            <Table caption="Chambres et dépendances">
              <thead>
                <tr>
                  <th>Bien</th>
                  <th>Surface</th>
                  <th>Occupation</th>
                </tr>
              </thead>
              <tbody>
                {p.children.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to={org.to(`logements/${c.id}`)}>{c.label}</Link>
                      <span className="pat-sub">
                        {c.kind_label}
                        {c.archived_at ? " · archivé" : ""}
                      </span>
                    </td>
                    <td className="num">{c.surface_m2 !== null ? `${c.surface_m2.toLocaleString("fr-FR")} m²` : "—"}</td>
                    <td>
                      <OccupationBadge occupation={c.occupation} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      ) : null}
      <Card title="Baux">
        {p.leases.length === 0 ? (
          <p className="muted">Aucun bail enregistré pour ce bien.</p>
        ) : (
          <Table caption="Baux du bien">
            <thead>
              <tr>
                <th>Période</th>
                <th>Locataires</th>
                <th className="right">Loyer</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {p.leases.map((l) => (
                <tr key={l.id}>
                  <td>
                    <Link to={org.to(`baux/${l.id}`)}>
                      Depuis le {date(l.start_on)}
                      {l.end_on ? ` jusqu’au ${date(l.end_on)}` : ""}
                    </Link>
                    {l.imported ? <span className="pat-sub">Location reprise dans Bailivo</span> : null}
                  </td>
                  <td>{l.tenants ?? "—"}</td>
                  <td className="right num">
                    {money(l.rent_cents)}
                    <span className="pat-sub">+ {money(l.charges_cents)} de charges</span>
                  </td>
                  <td>{LEASE_STATUS[l.status] ?? l.status}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card title="Campagnes de location">
        {p.campaigns.length === 0 ? (
          <p className="muted">Aucune campagne pour ce bien.</p>
        ) : (
          <ul className="list-plain">
            {p.campaigns.map((c) => (
              <li key={c.id}>
                <Link to={org.to(`campagnes/${c.id}`)}>{c.title}</Link>{" "}
                <span className="muted small">
                  · {CAMPAIGN_STATUS[c.status] ?? c.status} · {money(c.rent_cents)} · créée le {date(c.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Modal open={adding} onClose={() => setAdding(false)} title="Ajouter une chambre ou une dépendance">
        {adding ? <PropertyForm preset={{ kind: "chambre", parent_id: p.id, building_id: p.building_id ?? "", rent_hint_cents: null }} onDone={() => setAdding(false)} onCancel={() => setAdding(false)} /> : null}
      </Modal>
    </div>
  );
}
